import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ColorModeService } from '@coreui/angular';
import { delay, filter, map, tap } from 'rxjs/operators';
import { AccountsService, AuthService, TokenService } from './auth';
import { BaseService, SpinComponent } from './services';
import { DirectivesModule, PipesModule, SharedModule } from './shared';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  imports: [RouterOutlet, SpinComponent, SharedModule, DirectivesModule, PipesModule],
})
export class AppComponent extends BaseService implements OnInit {
  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);

  readonly #colorModeService = inject(ColorModeService);
  readonly #authService = inject(AuthService);
  readonly #tokenService = inject(TokenService);
  readonly #accountService = inject(AccountsService);

  constructor() {
    super();
    this.#colorModeService.localStorageItemName.set('erpclass-dash-theme');
    this.#colorModeService.eventName.set('erpclass-dash-colorScheme');
    const storedTheme: string | null = this.#colorModeService.getStoredTheme('erpclass-dash-theme');
    this.#colorModeService.colorMode.set(storedTheme ?? 'auto');
  }

  ngOnInit(): void {
    this.#authService.initializeScheduler();
    this.#syncDarkThemeBodyClass();
    if (this.#tokenService.token || this.#tokenService.refreshToken) {
      void this.#accountService.loadAccountIds().catch((): void => {
        // Guards and refresh handling own invalid persisted sessions.
      });
    }
    this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
    });

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        map((params) => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
        filter((theme) => ['dark', 'light', 'auto'].includes(theme)),
        tap((theme) => {
          this.#colorModeService.colorMode.set(theme);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  /**
   * Mirrors `html[data-coreui-theme]` (the ColorModeService source of truth) onto
   * `body.c-dark-theme` for legacy CSS/token selectors and chart theme detection.
   * index.html ships `body.c-dark-theme` statically to avoid a flash-of-light before
   * bootstrap; without this sync it never reflects the real, resolved theme.
   */
  #syncDarkThemeBodyClass(): void {
    const root: HTMLElement = document.documentElement;
    const apply = (): void => {
      document.body.classList.toggle('c-dark-theme', root.dataset['coreuiTheme'] === 'dark');
    };
    apply();
    const observer: MutationObserver = new MutationObserver(apply);
    observer.observe(root, { attributes: true, attributeFilter: ['data-coreui-theme'] });
    this.#destroyRef.onDestroy(() => observer.disconnect());
  }
}
